/** Alineado con `apps/api/.../recurring-expense.dates.ts` (vista previa en cliente). */

export type ExpenseRecurrenceFrequency =
  | "monthly"
  | "bimonthly"
  | "quarterly"
  | "semiannual"
  | "annual";

export function monthsStepForFrequency(f: ExpenseRecurrenceFrequency): number {
  switch (f) {
    case "monthly":
      return 1;
    case "bimonthly":
      return 2;
    case "quarterly":
      return 3;
    case "semiannual":
      return 6;
    case "annual":
      return 12;
    default: {
      const _e: never = f;
      return _e;
    }
  }
}

export function addCalendarMonths(isoDate: string, deltaMonths: number): string {
  const parts = isoDate.split("-").map((x) => Number.parseInt(x, 10));
  const y = parts[0] ?? 1970;
  const mo = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const totalMonths = y * 12 + (mo - 1) + deltaMonths;
  const ny = Math.floor(totalMonths / 12);
  const nm = (totalMonths % 12) + 1;
  const lastDay = new Date(ny, nm, 0).getDate();
  const nd = Math.min(d, lastDay);
  return `${ny}-${String(nm).padStart(2, "0")}-${String(nd).padStart(2, "0")}`;
}

export function listOccurrenceDates(
  start: string,
  end: string,
  stepMonths: number,
): string[] {
  if (stepMonths < 1) return [];
  const out: string[] = [];
  let cur = start;
  while (cur <= end) {
    out.push(cur);
    cur = addCalendarMonths(cur, stepMonths);
    if (out.length > 10_000) break;
  }
  return out;
}

/**
 * Cuántos cargos mensuales se generan para “cargo fijo mensual sin fecha fin” en el formulario.
 * La API sigue necesitando un `endsOn`; usamos este horizonte por defecto.
 */
export const MONTHLY_FIXED_MATERIALIZED_COUNT = 24;

/** Alineado con `EXPENSE_SERIES_MATERIALIZE_MONTHS_INITIAL` en la API (vista previa serie sin fin). */
export const EXPENSE_SERIES_MATERIALIZE_MONTHS_INITIAL = 3;

/** Última fecha (inclusive) de la ventana de cargos mensuales fijos. */
export function endsOnForMonthlyFixedWindow(occurredOn: string): string {
  return addCalendarMonths(occurredOn, MONTHLY_FIXED_MATERIALIZED_COUNT - 1);
}

/** Meses de calendario inclusivos entre dos fechas ISO (YYYY-MM-DD). Mínimo 1. */
export function inclusiveCalendarMonthSpan(startIso: string, endIso: string): number {
  const ps = startIso.split("-").map((x) => Number.parseInt(x, 10));
  const pe = endIso.split("-").map((x) => Number.parseInt(x, 10));
  const y1 = ps[0] ?? 0;
  const m1 = ps[1] ?? 1;
  const y2 = pe[0] ?? 0;
  const m2 = pe[1] ?? 1;
  const startIdx = y1 * 12 + (m1 - 1);
  const endIdx = y2 * 12 + (m2 - 1);
  return Math.max(1, endIdx - startIdx + 1);
}

export function splitTotalMinorAcrossOccurrences(
  totalMinor: number,
  n: number,
): number[] {
  if (n <= 0) return [];
  const base = Math.floor(totalMinor / n);
  const rem = totalMinor - base * n;
  return Array.from({ length: n }, (_, i) =>
    i === n - 1 ? base + rem : base,
  );
}
